/**
 * ProjectDetailPage Component for LOOPINO AI Platform
 * Mobile page for viewing and managing project details and files
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import './ProjectDetailPage.css';
import { buildMediaCounters } from './utils';
import CreateProjectModal, { FileDescriptionModal } from './CreateProjectModal';
import useLanguage from '../../../hooks/useLanguage';
import aiStorageService from '../../../services/AIStorageService';

// Import icons
import iconArrowLeft from '../../../assets/images/icons/icon-arrow-left.svg';
import iconUpload from '../../../assets/images/icons/icon-upload.svg';
import iconImage from '../../../assets/images/icons/icon-image.svg';
import iconVideo from '../../../assets/images/icons/icon-video-generator.svg';
import iconDelete from '../../../assets/images/icons/icon-delete.svg';
import iconEdit from '../../../assets/images/icons/icon-edit.svg';
import placeholderImage from '../../../assets/images/placeholder-post-image.png';
import { Skeleton } from '../../effects';

const ProjectDetailPage = ({ 
  projectId, 
  onBack 
}) => {
  const { t, language } = useLanguage();
  // State management
  const [project, setProject] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showFileDescriptionModal, setShowFileDescriptionModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [imageLoadStates, setImageLoadStates] = useState(() => new Map());
  const fileInputRef = useRef(null);

  const setImageLoadState = useCallback((mediaId, status) => {
    if (!mediaId) {
      return;
    }

    setImageLoadStates((prev) => {
      const currentStatus = prev.get(mediaId);
      if (currentStatus === status) {
        return prev;
      }

      const next = new Map(prev);
      next.set(mediaId, status);
      return next;
    });
  }, []);

  // Handle image load error
  const handleImageError = useCallback((event, mediaId) => {
    if (event?.target) {
      event.target.src = placeholderImage;
    }
    setImageLoadState(mediaId, 'error');
  }, [setImageLoadState]);

  // Handle image load completion
  const handleImageLoad = useCallback((mediaId) => {
    setImageLoadState(mediaId, 'loaded');
  }, [setImageLoadState]);

  const loadProject = useCallback(async ({ silent = false } = {}) => {
    if (!projectId) {
      return;
    }

    try {
      if (!silent) {
        setIsLoading(true);
      }
      setError(null);
      const normalizedProject = await aiStorageService.getProject(projectId);
      if (!normalizedProject) {
        throw new Error(t('aiStorage.details.errors.notFound'));
      }
      setProject(normalizedProject);
    } catch (err) {
      console.error('[ProjectDetailPage] Failed to load project', err);
      const message = err?.message || t('aiStorage.messages.genericError');
      setError(t('aiStorage.details.errors.load', { error: message }));
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  }, [collectMediaDetails, get, projectId, t]);

  useEffect(() => {
    loadProject();
  }, [loadProject]);

  const handleUploadClick = useCallback(() => {
    if (isUploading) {
      return;
    }

    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [isUploading]);

  const uploadFilesToAlbum = useCallback(async (selectedFiles) => {
    if (!projectId || !selectedFiles || selectedFiles.length === 0) {
      return;
    }

    setIsUploading(true);
    setError(null);

    const toArray = Array.from(selectedFiles).filter(Boolean);
    if (!toArray.length) {
      setIsUploading(false);
      return;
    }

    try {
      const updatedProject = await aiStorageService.uploadFiles(projectId, toArray);
      setProject(updatedProject);
    } catch (uploadErr) {
      console.error('[ProjectDetailPage] Failed to upload media', uploadErr);
      const message = uploadErr?.message || t('aiStorage.messages.genericError');
      setError(t('aiStorage.details.errors.upload', { error: message }));
      await loadProject({ silent: true });
    } finally {
      setIsUploading(false);
    }
  }, [loadProject, projectId, t]);

  const handleFileInputChange = useCallback((event) => {
    const { files } = event.target;
    if (!files || files.length === 0) {
      return;
    }

    uploadFilesToAlbum(files);

    // Reset input so the same file can be selected again if needed
    event.target.value = '';
  }, [uploadFilesToAlbum]);

  const handleDeleteFile = useCallback(async (fileId) => {
    if (!projectId || !fileId) {
      return;
    }

    setProject((prev) => {
      if (!prev) {
        return prev;
      }

      const filteredFiles = prev.files.filter((file) => file.id !== fileId);
      const counters = buildMediaCounters(filteredFiles);

      return {
        ...prev,
        files: filteredFiles,
        filesCount: filteredFiles.length,
        imagesCount: counters.imagesCount,
        videosCount: counters.videosCount,
      };
    });

    try {
      const updatedProject = await aiStorageService.removeAsset(projectId, fileId);
      setProject(updatedProject);
    } catch (err) {
      console.error('[ProjectDetailPage] Failed to remove media from album', err);
      const message = err?.message || t('aiStorage.messages.genericError');
      setError(t('aiStorage.details.errors.delete', { error: message }));
      await loadProject({ silent: true });
    }
  }, [loadProject, projectId, t]);

  // Handle edit project
  const handleEditProject = useCallback(() => {
    console.log('[ProjectDetailPage] Opening edit modal for project:', project?.id);
    setShowEditModal(true);
  }, [project]);

  // Handle edit modal close
  const handleCloseEditModal = useCallback(() => {
    setShowEditModal(false);
  }, []);

  // Handle project update
  const handleProjectUpdated = useCallback((updatedProject) => {
    console.log('[ProjectDetailPage] Project updated:', updatedProject);

    setShowEditModal(false);

    if (updatedProject) {
      setProject((prev) => ({ ...(prev || {}), ...updatedProject }));
    }

    loadProject({ silent: true });
  }, [loadProject]);

  // Handle file description click
  const handleFileDescriptionClick = useCallback((file) => {
    console.log('[ProjectDetailPage] Opening file description modal for:', file);
    setSelectedFile(file);
    setShowFileDescriptionModal(true);
  }, []);

  // Handle file description modal close
  const handleCloseFileDescriptionModal = useCallback(() => {
    setShowFileDescriptionModal(false);
    setSelectedFile(null);
  }, []);

  // Handle file update
  const handleFileUpdated = useCallback((updatedFile) => {
    console.log('[ProjectDetailPage] File updated:', updatedFile);

    if (!updatedFile) {
      return;
    }

    setProject((prev) => {
      if (!prev) {
        return prev;
      }

      const updatedFiles = (prev.files || []).map((file) => (
        file.id === updatedFile.id ? { ...file, ...updatedFile } : file
      ));

      return {
        ...prev,
        files: updatedFiles,
      };
    });
  }, []);

  const detailDateFormatter = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(language || 'it', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (err) {
      console.warn('[ProjectDetailPage] Falling back to default date formatter', err);
      return new Intl.DateTimeFormat('it', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  }, [language]);

  const formatDate = useCallback((date) => {
    if (!date) {
      return '--';
    }

    const parsedDate = new Date(date);
    if (Number.isNaN(parsedDate.getTime())) {
      return '--';
    }

    return detailDateFormatter.format(parsedDate);
  }, [detailDateFormatter]);

  const sizeUnits = useMemo(() => {
    const units = t('aiStorage.details.files.sizeUnits', { returnObjects: true });
    if (Array.isArray(units) && units.length >= 4) {
      return units;
    }
    return ['Bytes', 'KB', 'MB', 'GB'];
  }, [t]);

  const numberFormatter = useMemo(() => {
    try {
      return new Intl.NumberFormat(language || 'it', {
        maximumFractionDigits: 2,
        minimumFractionDigits: 0
      });
    } catch (err) {
      console.warn('[ProjectDetailPage] Falling back to default number formatter', err);
      return new Intl.NumberFormat('it', {
        maximumFractionDigits: 2,
        minimumFractionDigits: 0
      });
    }
  }, [language]);

  const formatFileSize = useCallback((bytes) => {
    if (!bytes) {
      return `0 ${sizeUnits[1] || 'KB'}`;
    }

    const size = Number(bytes);
    if (!Number.isFinite(size) || size <= 0) {
      return `0 ${sizeUnits[1] || 'KB'}`;
    }

    const k = 1024;
    const index = Math.min(Math.floor(Math.log(size) / Math.log(k)), sizeUnits.length - 1);
    const value = size / Math.pow(k, index);
    return `${numberFormatter.format(value)} ${sizeUnits[index]}`;
  }, [numberFormatter, sizeUnits]);

  const renderLoadingSkeleton = () => {
    const placeholders = Array.from({ length: 4 });

    return (
      <div className="mobile-project-detail">
        <div className="mobile-project-detail-content">
          <div className="mobile-project-detail-header">
            <Skeleton variant="circle" width={40} height={40} shimmer />
            <Skeleton width="55%" height={24} shimmer style={{ marginLeft: 12, marginRight: 12 }} />
            <Skeleton variant="circle" width={40} height={40} shimmer />
          </div>

          <div className="mobile-project-detail-info">
            <div className="mobile-project-detail-description">
              <Skeleton width="70%" height={18} shimmer style={{ marginBottom: 8 }} />
              <Skeleton count={3} width="100%" height={12} shimmer />
            </div>
          </div>

          <div className="mobile-project-detail-files">
            <div className="mobile-project-detail-files-list">
              {placeholders.map((_, index) => (
                <div key={`project-file-skeleton-${index}`} className="mobile-project-file-item">
                  <div className="mobile-project-file-preview" style={{ minHeight: 96 }}>
                    <Skeleton variant="rounded" width="100%" height="100%" shimmer />
                  </div>
                  <div className="mobile-project-file-info">
                    <Skeleton width="75%" height={16} shimmer style={{ marginBottom: 6 }} />
                    <Skeleton width="50%" height={12} shimmer />
                  </div>
                  <div className="mobile-project-file-delete" style={{ pointerEvents: 'none' }}>
                    <Skeleton variant="circle" width={32} height={32} shimmer />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render file item
  const renderFileItem = (file) => {
    const media = file || {};
    const isImage = media.type === 'image';
    const isVideo = media.type === 'video';
    const fallbackNames = ['Media'];
    let displayName = media.displayName || media.aiEnhancedTitle || media.name;
    if (!displayName || fallbackNames.includes(displayName.trim())) {
      displayName = t('aiStorage.details.files.untitled');
    }
    const hasDescription = Boolean(media.description && media.description.trim().length > 0);
    const fileSize = media.sizeBytes ?? media.size ?? (media.sizeKb ? media.sizeKb * 1024 : 0);
    const uploadedAt = media.uploadedAt || media.createdAt || media.created_at || media.updatedAt || null;
    const previewUrl = media.url || media.thumbnailUrl || placeholderImage;
    const imageStatus = imageLoadStates.get(media.id) || 'loading';
    const imageClassNames = ['mobile-project-file-image', `is-${imageStatus}`];

    return (
      <div 
        key={media.id} 
        className="mobile-project-file-item"
        onClick={() => handleFileDescriptionClick(media)}
        style={{ cursor: 'pointer' }}
      >
        <div className="mobile-project-file-preview">
          {isImage ? (
            <img 
              src={previewUrl} 
              alt={displayName}
              className={imageClassNames.join(' ')}
              loading="lazy"
              onError={(event) => handleImageError(event, media.id)}
              onLoad={() => handleImageLoad(media.id)}
            />
          ) : isVideo ? (
            <div className="mobile-project-file-video">
              <video 
                src={previewUrl}
                className="mobile-project-file-video-element"
                muted
                preload="metadata"
              />
              <div className="mobile-project-file-video-overlay">
                <img src={iconVideo} alt={t('aiStorage.details.alt.video')} />
              </div>
            </div>
          ) : (
            <div className="mobile-project-file-placeholder">
              <img src={iconImage} alt={t('aiStorage.details.alt.file')} />
            </div>
          )}
        </div>
        
        <div className="mobile-project-file-info">
          <div className="mobile-project-file-name">
            {displayName}
            {hasDescription && (
              <span className="mobile-project-file-description-indicator">✓</span>
            )}
          </div>
          <div className="mobile-project-file-meta">
            <span>{formatFileSize(fileSize)}</span>
            <span>•</span>
            <span>{formatDate(uploadedAt)}</span>
            {hasDescription && (
              <span>•</span>
            )}
            {hasDescription && (
              <span className="mobile-project-file-has-description">{t('aiStorage.details.files.hasDescription')}</span>
            )}
          </div>
        </div>
        
        <button 
          className="mobile-project-file-delete"
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteFile(media.id);
          }}
          aria-label={t('aiStorage.details.accessibility.deleteFile')}
        >
          <img src={iconDelete} alt={t('aiStorage.details.alt.delete')} />
        </button>
      </div>
    );
  };



  // Render loading state
  if (isLoading) {
    return renderLoadingSkeleton();
  }

  // Render error state
  if (error && !project) {
    return (
      <div className="mobile-project-detail">
        <div className="mobile-project-detail-error">
          <p>{error}</p>
          <button onClick={loadProject}>{t('aiStorage.actions.retry')}</button>
        </div>
      </div>
    );
  }

  if (!project) {
    return null;
  }

  const fallbackAlbumTitles = ['Album senza titolo', 'Untitled album'];
  const trimmedTitle = (project.title || '').trim();
  const albumTitle = trimmedTitle && !fallbackAlbumTitles.includes(trimmedTitle)
    ? project.title
    : t('aiStorage.list.defaults.untitledAlbum');

  return (
    <div className="mobile-project-detail">
      <div className="mobile-project-detail-content">
        {/* Header */}
        <div className="mobile-project-detail-header">
          <button 
            className="mobile-project-detail-back"
            onClick={onBack}
            aria-label={t('aiStorage.details.accessibility.back')}
          >
            <img src={iconArrowLeft} alt={t('aiStorage.details.alt.back')} />
          </button>
          <h1 className="mobile-project-detail-title">{albumTitle}</h1>
          <button 
            className="mobile-project-detail-edit"
            onClick={handleEditProject}
            aria-label={t('aiStorage.details.accessibility.edit')}
          >
            <img src={iconEdit} alt={t('aiStorage.details.alt.edit')} />
          </button>
        </div>

        {/* Error message */}
        {error && (
          <div className="mobile-project-detail-error-banner">
            {error}
          </div>
        )}

        {/* Project info */}
        <div className="mobile-project-detail-info">
          <div className="mobile-project-detail-description">
            <p>{project.aiGeneratedDescription || project.description || t('aiStorage.messages.noDescription')}</p>
          </div>
        </div>

        {/* Files section */}
        <div className="mobile-project-detail-files">
          {/* Files list */}
          {project.files && project.files.length > 0 ? (
            <div className="mobile-project-detail-files-list">
              {project.files.map(renderFileItem)}
            </div>
          ) : (
            <div className="mobile-project-detail-empty-files">
              <div className="mobile-project-detail-empty-icon">
                <img src={iconImage} alt={t('aiStorage.details.alt.empty')} />
              </div>
              <p>{t('aiStorage.details.empty.title')}</p>
              <span>{t('aiStorage.details.empty.subtitle')}</span>
            </div>
          )}
        </div>

        {/* Floating add media button */}
        <button 
          className="mobile-project-detail-upload-fab"
          onClick={handleUploadClick}
          disabled={isUploading}
          aria-label={t('aiStorage.details.accessibility.upload')}
        >
          <div className="mobile-project-detail-upload-fab-icon">
            {isUploading ? (
              <div className="mobile-project-detail-upload-fab-spinner"></div>
            ) : (
              <img src={iconUpload} alt={t('aiStorage.details.alt.upload')} />
            )}
          </div>
        </button>

        {/* Upload progress overlay */}
        {isUploading && (
          <div className="mobile-project-detail-upload-progress-overlay">
            <div className="mobile-project-detail-upload-progress">
              <span className="mobile-project-detail-upload-progress-text">
                {t('aiStorage.status.uploading')}
              </span>
            </div>
          </div>
        )}

        {/* Edit project modal */}
        <CreateProjectModal 
          isOpen={showEditModal}
          onClose={handleCloseEditModal}
          onProjectCreated={handleProjectUpdated}
          editMode={true}
          existingProject={project}
        />

        {/* File description modal */}
        <FileDescriptionModal 
          isOpen={showFileDescriptionModal}
          onClose={handleCloseFileDescriptionModal}
          onFileUpdated={handleFileUpdated}
          file={selectedFile}
        />

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*"
          style={{ display: 'none' }}
          multiple
          onChange={handleFileInputChange}
        />
      </div>
    </div>
  );
};

export default ProjectDetailPage;
